import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, UserPlus, Users } from "lucide-react";

const EMPTY = {
  name: "",
  email: "",
  password: "",
  role: "teacher",
  assigned_class: "",
  linked_child_id: "",
};

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [children, setChildren] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const [u, c] = await Promise.all([
        api.get("/users"),
        api.get("/children"),
      ]);
      setUsers(u.data || []);
      setChildren(c.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unable to load users");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const classes = useMemo(() => {
    const values = children
      .map((c) => c.class_name || c.class || c.grade)
      .filter(Boolean);
    return [...new Set(values)].sort();
  }, [children]);

  const change = (key, value) =>
    setForm((f) => ({ ...f, [key]: value }));

  const createUser = async (e) => {
    e.preventDefault();

    if (!form.name || !form.email || !form.password) {
      toast.error("Name, email and password are required");
      return;
    }

    if (form.role === "teacher" && !form.assigned_class) {
      toast.error("Select teacher's assigned class");
      return;
    }

    if (form.role === "parent" && !form.linked_child_id) {
      toast.error("Select the child linked to this parent");
      return;
    }

    const payload = {
      name: form.name.trim(),
      email: form.email.trim().toLowerCase(),
      password: form.password,
      role: form.role,
      assigned_class:
        form.role === "teacher" ? form.assigned_class : null,
      linked_child_id:
        form.role === "parent" ? form.linked_child_id : null,
    };

    try {
      setLoading(true);
      await api.post("/users", payload);
      toast.success("User account created");
      setForm(EMPTY);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unable to create user");
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete account for ${u.name || u.email}?`)) return;

    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unable to delete user");
    }
  };

  const roleName = (role) => {
    if (role === "admin") return "Principal / Admin";
    if (role === "teacher") return "Teacher";
    if (role === "front_desk") return "Front Desk";
    if (role === "parent") return "Parent";
    return role;
  };

  return (
    <div className="p-4 md:p-8 space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
          <Users size={28} />
          User Management
        </h1>
        <p className="text-muted-foreground mt-1">
          Create and manage Principal, Teacher, Front Desk and Parent logins.
        </p>
      </div>

      <div className="bg-white rounded-3xl border border-border p-5 md:p-6">
        <div className="font-bold text-lg mb-5 flex items-center gap-2">
          <UserPlus size={20} />
          Create New Login
        </div>

        <form
          onSubmit={createUser}
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
        >
          <div>
            <label className="text-sm font-semibold">Full Name</label>
            <input
              className="mt-1 w-full border rounded-xl px-4 py-3"
              value={form.name}
              onChange={(e) => change("name", e.target.value)}
              placeholder="e.g. Anita Sharma"
            />
          </div>

          <div>
            <label className="text-sm font-semibold">Email / Login ID</label>
            <input
              type="email"
              className="mt-1 w-full border rounded-xl px-4 py-3"
              value={form.email}
              onChange={(e) => change("email", e.target.value)}
              placeholder="name@kidzkulam.in"
            />
          </div>

          <div>
            <label className="text-sm font-semibold">Password</label>
            <input
              type="password"
              className="mt-1 w-full border rounded-xl px-4 py-3"
              value={form.password}
              onChange={(e) => change("password", e.target.value)}
              placeholder="Temporary password"
            />
          </div>

          <div>
            <label className="text-sm font-semibold">Role</label>
            <select
              className="mt-1 w-full border rounded-xl px-4 py-3 bg-white"
              value={form.role}
              onChange={(e) =>
                setForm({
                  ...form,
                  role: e.target.value,
                  assigned_class: "",
                  linked_child_id: "",
                })
              }
            >
              <option value="admin">Principal / Admin</option>
              <option value="teacher">Teacher</option>
              <option value="front_desk">Front Desk / Reception</option>
              <option value="parent">Parent</option>
            </select>
          </div>

          {form.role === "teacher" && (
            <div>
              <label className="text-sm font-semibold">Assigned Class</label>
              <select
                className="mt-1 w-full border rounded-xl px-4 py-3 bg-white"
                value={form.assigned_class}
                onChange={(e) => change("assigned_class", e.target.value)}
              >
                <option value="">Select class</option>
                {classes.map((cls) => (
                  <option key={cls} value={cls}>
                    {cls}
                  </option>
                ))}
              </select>
            </div>
          )}

          {form.role === "parent" && (
            <div>
              <label className="text-sm font-semibold">Linked Child</label>
              <select
                className="mt-1 w-full border rounded-xl px-4 py-3 bg-white"
                value={form.linked_child_id}
                onChange={(e) => change("linked_child_id", e.target.value)}
              >
                <option value="">Select child</option>
                {children.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} — {c.class_name || c.class || ""}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="md:col-span-2">
            <button
              disabled={loading}
              className="px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold disabled:opacity-50"
            >
              {loading ? "Creating..." : "Create Account"}
            </button>
          </div>
        </form>
      </div>

      <div className="bg-white rounded-3xl border border-border overflow-hidden">
        <div className="p-5 font-bold text-lg">
          Existing Users ({users.length})
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-5 py-3">Name</th>
                <th className="text-left px-5 py-3">Email</th>
                <th className="text-left px-5 py-3">Role</th>
                <th className="text-left px-5 py-3">Class / Child</th>
                <th className="text-right px-5 py-3">Action</th>
              </tr>
            </thead>

            <tbody>
              {users.map((u) => {
                const child = children.find(
                  (c) => c.id === u.linked_child_id
                );

                return (
                  <tr key={u.id} className="border-t">
                    <td className="px-5 py-4 font-medium">
                      {u.name || "-"}
                    </td>
                    <td className="px-5 py-4">{u.email}</td>
                    <td className="px-5 py-4">
                      {roleName(u.role)}
                    </td>
                    <td className="px-5 py-4">
                      {u.role === "teacher"
                        ? u.assigned_class || "-"
                        : u.role === "parent"
                        ? child?.name || "-"
                        : "-"}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <button
                        onClick={() => deleteUser(u)}
                        className="p-2 rounded-lg text-destructive hover:bg-destructive/10"
                        title="Delete user"
                      >
                        <Trash2 size={17} />
                      </button>
                    </td>
                  </tr>
                );
              })}

              {!users.length && (
                <tr>
                  <td
                    colSpan="5"
                    className="px-5 py-10 text-center text-muted-foreground"
                  >
                    No users found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
